import { useEffect, useState } from "react";
import { X, Loader2, AlertTriangle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import { formatRupiah, policyLineText } from "@/components/booking/bookingMeta";

// Customer booking cancellation + refund confirmation (Step 20).
export default function CancelBookingModal({ open, booking, onClose, onSuccess }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);

  useEffect(() => {
    if (!open || !booking) return;
    setPreview(null); setReason(""); setShowPolicy(false);
    setLoading(true);
    apiClient.get(`/bookings/${booking.id}/cancellation-preview`)
      .then((r) => setPreview(r.data))
      .catch((err) => toast.error(formatApiErrorDetail(err?.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [open, booking]);

  if (!open || !booking) return null;

  const confirm = async () => {
    setSubmitting(true);
    try {
      await apiClient.post(`/bookings/${booking.id}/cancel`, { reason: reason.trim() || null });
      toast.success("Booking dibatalkan.");
      onSuccess?.();
      onClose?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const p = preview;
  const pol = p?.cancellation_policy || {};

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 backdrop-blur-sm p-4" data-testid="cancel-booking-modal">
      <div className="w-full max-w-lg bg-card border border-border rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-card">
          <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
            <AlertTriangle size={18} className="text-rose-500" /> Pembatalan Booking
          </h3>
          <button onClick={onClose} data-testid="cancel-modal-close" className="text-muted-foreground hover:text-foreground">
            <X size={20} />
          </button>
        </div>

        {loading || !p ? (
          <div className="grid place-items-center py-16"><Loader2 className="animate-spin text-primary" size={26} /></div>
        ) : !p.can_cancel ? (
          <div className="p-6 text-center" data-testid="cancel-not-allowed">
            <p className="text-sm text-rose-600">{p.reason_if_not || "Booking ini tidak dapat dibatalkan."}</p>
            <button onClick={onClose} className="mt-5 px-5 py-2.5 rounded-full border border-border text-sm font-semibold hover:bg-secondary/60">Kembali</button>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            <div>
              <p className="font-heading text-base font-semibold">{p.property_name || booking.property_name}</p>
              <p className="text-sm text-muted-foreground">Kamar {p.room_number || booking.room_number}</p>
            </div>

            <div className="rounded-xl border border-border bg-secondary/20 p-4 text-sm space-y-1.5" data-testid="cancel-detail">
              <Row label="Harga Kamar" value={formatRupiah(p.room_price_total)} />
              <Row label="Deposit" value={formatRupiah(p.deposit_amount)} />
              <div className="border-t border-border my-1.5" />
              <Row label="Total Sudah Dibayar" value={formatRupiah(p.total_paid)} strong testid="cancel-total-paid" />
              <Row label="Sisa Harga Kamar" value={formatRupiah(p.room_amount_remaining)} muted />
              <Row label="Sisa Deposit" value={formatRupiah(p.deposit_amount_remaining)} muted />
            </div>

            <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 text-sm space-y-1.5" data-testid="cancel-refund-estimate">
              <div className="flex items-center gap-2 text-primary font-semibold">
                <ShieldCheck size={15} /> Estimasi Refund
              </div>
              <Row label="Refund Harga Kamar" value={formatRupiah(p.estimated_room_refund)} testid="cancel-room-refund" />
              <Row label="Refund Deposit" value={formatRupiah(p.estimated_deposit_refund)} testid="cancel-deposit-refund" />
              <div className="border-t border-primary/20 my-1" />
              <Row label="Total Estimasi Refund" value={formatRupiah(p.estimated_total_refund)} strong testid="cancel-total-refund" />
              <p className="text-xs text-muted-foreground pt-1">
                Refund dihitung dari uang yang benar-benar sudah dibayar (bukan total harga booking).
              </p>
            </div>

            <div>
              <button type="button" onClick={() => setShowPolicy((v) => !v)}
                data-testid="cancel-policy-toggle"
                className="text-sm font-semibold text-primary hover:underline">
                {showPolicy ? "Sembunyikan" : "Detail"} Kebijakan Pembatalan
              </button>
              {showPolicy && (
                <div className="mt-2 rounded-xl border border-border bg-secondary/20 p-3 text-sm space-y-1" data-testid="cancel-policy-detail">
                  <Row label="Harga Kamar" value={policyLineText(pol.room_refund_type, pol.room_refund_rate)} />
                  <Row label="Deposit" value={policyLineText(pol.deposit_refund_type, pol.deposit_refund_rate)} />
                </div>
              )}
            </div>

            <div>
              <label className="text-sm font-medium text-foreground">Alasan Pembatalan</label>
              <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                data-testid="cancel-reason-input"
                placeholder="Opsional — beri tahu pemilik alasan pembatalan"
                className="mt-1.5 w-full rounded-xl border border-border bg-secondary/40 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-primary focus:bg-background transition-colors" />
            </div>

            <div className="flex gap-2 pt-1">
              <button onClick={confirm} disabled={submitting}
                data-testid="cancel-confirm-button"
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-rose-600 text-white font-semibold hover:bg-rose-700 transition-colors disabled:opacity-60">
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <AlertTriangle size={15} />}
                Konfirmasi Pembatalan
              </button>
              <button onClick={onClose} disabled={submitting}
                data-testid="cancel-back-button"
                className="px-5 py-2.5 rounded-full border border-border text-sm font-semibold hover:bg-secondary/60 transition-colors">
                Kembali
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, strong, muted, testid }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className={muted ? "text-muted-foreground text-xs" : "text-muted-foreground"}>{label}</span>
      <span className={`${strong ? "font-bold text-foreground" : "font-medium text-foreground"}`} data-testid={testid}>{value}</span>
    </div>
  );
}
